/**
 * AdaptiveCountdown — the §24 processing UX (interim). While an upload is in
 * flight it shows "Analysing …" with a remaining-time estimate that DECELERATES
 * toward a floor and never reaches zero before the response arrives; the parent
 * snaps to the report on resolve. The estimate is derived client-side from file
 * size (see utils/estimateProcessing.js); the exponential deceleration masks
 * estimate error so the countdown stays confident regardless of the true time.
 * @module dashboard/components/AdaptiveCountdown
 */
import { useEffect, useRef, useState } from 'react';

const TICK_MS = 250;
// Asymptote floor: the displayed remaining never drops below this while waiting.
const FLOOR_S = 3;

/** Format remaining seconds: minutes above 60s, else seconds. */
export function formatRemaining(seconds) {
  const s = Math.max(0, Math.ceil(seconds));
  if (s > 60) {
    const mins = Math.max(1, Math.round(s / 60));
    return `~${mins} ${mins === 1 ? 'minute' : 'minutes'} remaining`;
  }
  return `~${s} ${s === 1 ? 'second' : 'seconds'} remaining`;
}

export function AdaptiveCountdown({ estimateSeconds, remainingSeconds, projectName }) {
  const est = Number.isFinite(estimateSeconds) && estimateSeconds > 0 ? estimateSeconds : FLOOR_S;
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);

  useEffect(() => {
    startRef.current = Date.now();
    const id = setInterval(() => {
      setElapsed((Date.now() - startRef.current) / 1000);
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // UPLOAD-WIRE B: when the parent supplies the honest wall-clock/progress
  // number (remainingSeconds), render it directly — the parent owns the tick,
  // the monotonic clamp, and the single 202 reset. Without it (legacy
  // callers/pre-first-progress), the original decelerating estimate stands.
  const driven = Number.isFinite(remainingSeconds) && remainingSeconds > 0;

  // Exponential deceleration: remaining = est · e^(-elapsed / (est/3)), floored.
  // Approaches FLOOR_S asymptotically — keeps ticking down, slows near the
  // floor, and NEVER reaches zero while the request is pending.
  const tau = Math.max(1, est / 3);
  const legacyRemaining = Math.max(est * Math.exp(-elapsed / tau), FLOOR_S);
  const remaining = driven ? remainingSeconds : legacyRemaining;
  const label = `Analysing ${projectName || 'your schedule'}…`;

  return (
    <div className="upload-countdown" role="status" aria-busy="true" aria-live="polite">
      <p className="upload-countdown-label">{label}</p>
      <p className="upload-countdown-remaining">{formatRemaining(remaining)}</p>
    </div>
  );
}
